import { useState } from "react";
import { useWizard } from "components/elements/Wizard/useWizard";
import { useSpeckleProjects } from "../../lib/hooks/useSpeckleProjects";
import { getModelTextSearch } from "lib/actions/getModelTextSearch";
import { nameof } from "lib/actions/util/nameof";
import { ErrorMessage, Field, Formik, useFormikContext } from "formik";
import { SpeckleImportModel } from "../types/SpeckleImportModel";
import { SpeckleProjectModel } from "../types/SpeckleProjectModel";
import Loading from "components/elements/Loading/Loading";
import FormError from "components/elements/Forms/FormError";
import SpeckleTitle from "../SpeckleTitle";
import InputSearch from "components/elements/Input/InputSearch";
import SpeckleRadioInputProjects from "../controls/SpeckleRadioInputProjects";
import SpeckleRefreshButton from "../SpeckleRefreshButton";
import SpeckleBreadcrumbs, {
  SpeckleCrumb,
  SpeckleCrumbSeparator,
} from "../SpeckleBreadcrumbs";
import SpeckleCreateProjectForm from "./create/SpeckleCreateProjectForm";
import Button from "components/elements/Button/Button";
import {
  getClearedValuesForStep,
  SpeckleImportStep,
} from "components/modules/UploadDialog/SpeckleImportWizard/steps/getClearedValuesForStep";

export default function SpeckleImportProjectStep() {
  const [searchTerm, setSearchTerm] = useState("");
  const { values, isSubmitting, setValues, submitForm, setTouched, setErrors } =
    useFormikContext<SpeckleImportModel>();
  const { stepVariant, stepViewVariant, stepJump } = useWizard();
  const { speckleProjects, isLoading, hasError, refresh } = useSpeckleProjects(
    parseInt(values.connectedAccountId),
    values.workspaceId,
  );

  function handleStepJump(step: SpeckleImportStep) {
    const clearedValues = getClearedValuesForStep(step, values);
    setValues(clearedValues, false);
    setTouched({}, false);
    setErrors({});
    stepJump(step);
  }

  const variantCreate = "create";
  const isShowingCreate = stepVariant === variantCreate;
  const { search } = getModelTextSearch<SpeckleProjectModel>(
    searchTerm,
    (project) => project.name,
  );
  const projects = search(speckleProjects) || [];
  const projectItems =
    !isLoading &&
    !hasError &&
    projects.map((x) => ({
      value: x.model.id,
      label: x.model.name,
    }));

  function onRefresh() {
    refresh();
  }

  async function onSelect(value: string) {
    setValues(
      (values) => ({
        ...values,
        projectId: value,
        projectName: projects.find((x) => x.model.id === value)?.model.name,
      }),
      false,
    );
    await new Promise((res) => setTimeout(res, 0)); //hack: cannot submit immediately after setting values
    submitForm();
  }

  function onShowCreate() {
    stepViewVariant(variantCreate);
  }

  async function onCreated(created: SpeckleProjectModel) {
    setValues(
      (values) => ({
        ...values,
        projectId: created.id,
        projectName: created.name,
      }),
      false,
    );
    await new Promise((res) => setTimeout(res, 0)); //hack: cannot submit immediately after setting values
    submitForm();
  }

  return (
    <div className="grid grid-cols-2 gap-8">
      <div>
        <header>
          <SpeckleTitle />
          <h1 className="mt-10 text-2xl font-thin leading-loose">
            Select a <span className="font-normal">Speckle Project</span> <br />
            <span className="flex whitespace-nowrap">
              or&nbsp;
              <Button
                slim
                type="button"
                className="my-auto ml-2 h-8 text-sm"
                disabled={isShowingCreate}
                onClick={onShowCreate}
              >
                create project
              </Button>
            </span>
          </h1>
        </header>
        {isShowingCreate && (
          <section className="mt-8 rounded-xl bg-gray-50 p-6 pb-10">
            <h2 className="font-medium">Create a project in&nbsp;Speckle</h2>
            <SpeckleCreateProjectForm onCreated={onCreated} />
          </section>
        )}
      </div>
      <div className="space-y-5">
        <SpeckleBreadcrumbs>
          <SpeckleCrumb
            onClick={() => handleStepJump(SpeckleImportStep.Account)}
          >
            {values.connectedAccountId}
          </SpeckleCrumb>
          <SpeckleCrumbSeparator />
          <SpeckleCrumb
            onClick={() => handleStepJump(SpeckleImportStep.Workspace)}
          >
            {values.workspaceName}
          </SpeckleCrumb>
          <SpeckleCrumbSeparator />
          <SpeckleCrumb active>Projects</SpeckleCrumb>
        </SpeckleBreadcrumbs>
        <div className="flex items-center justify-between pr-5 text-xs">
          <span>{speckleProjects?.length ?? 0} projects available</span>
          <SpeckleRefreshButton onClick={onRefresh} />
        </div>
        <div className="h-[36rem] select-none space-y-8 overflow-y-auto pb-8 pr-4">
          <InputSearch
            defaultValue={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search projects"
          />
          <Field
            name={nameof(values, "projectId")}
            as={SpeckleRadioInputProjects}
            items={projectItems}
            onSelect={onSelect}
            disabled={isSubmitting || isShowingCreate}
          />
          {isLoading && <Loading />}
          {hasError && <span className="text-center">Error</span>}
        </div>
        <ErrorMessage
          name={nameof(values, "projectId")}
          component={FormError}
        />
      </div>
    </div>
  );
}
